// @ts-nocheck — pre-existing module, not in the codetab checkJs surface.
// checkpointGate.js — E3-WS2-T01 (code-tab-to-9).
// @arch:pure-engine
//
// Green-only capture/restore at the loop boundary. Wraps the existing
// apolo-api/src/agents/checkpoint.js primitives (isHonestGreen, captureCheckpoint,
// restoreLatestGreen, createCheckpointStore) into a loop-scoped gate.
//
// Flag `apolo_checkpoint_loop` default OFF. When OFF, createLoopCheckpointer
// returns a no-op pair — behavior byte-identical to today.
//
// PURE (Tier 1): no react/window/IPC. All I/O is injected (snapshot/restore/persist).

/**
 * Create a loop-scoped checkpointer pair (capture + restore).
 * When `enabled=false`, both are no-ops (byte-identical to today).
 *
 * @param {object} opts
 * @param {boolean} opts.enabled
 * @param {string}  opts.root
 * @param {Function} [opts.snapshot]  ({root, signal}) → string (diff/ref)
 * @param {Function} [opts.restore]  ({ref, root, resolveInside, signal}) → void
 * @param {Function} [opts.persist]  (checkpoint) → void (best-effort durable)
 * @param {Function} [opts.isHonestGreen]  (verdict) → boolean
 * @param {Function} [opts.captureCheckpoint]  checkpoint.js captureCheckpoint
 * @param {Function} [opts.restoreLatestGreen]  checkpoint.js restoreLatestGreen
 * @param {Function} [opts.createCheckpointStore]  checkpoint.js createCheckpointStore
 * @returns {{ onGreen: Function, onFail: Function, store: object|null }}
 */
export function createLoopCheckpointer({
  enabled = false,
  root,
  snapshot,
  restore,
  persist,
  isHonestGreen,
  captureCheckpoint,
  restoreLatestGreen,
  createCheckpointStore,
} = {}) {
  if (!enabled) {
    return {
      onGreen: async () => ({ ok: false, skipped: true }),
      onFail: async () => ({ ok: false, skipped: true }),
      store: null,
    };
  }

  const store = createCheckpointStore ? createCheckpointStore() : { checkpoints: [] };
  const _isHonestGreen = isHonestGreen || ((v) => v && (v.green === true || v.passed === true));
  const _captureCheckpoint = captureCheckpoint || (async (_verdict, ctx, deps) => {
    try {
      const ref = deps.snapshot ? await deps.snapshot({ root: ctx.root, signal: ctx.signal }) : null;
      const cp = { ref, taskId: ctx.taskId, at: new Date().toISOString() };
      if (deps.persist) try { deps.persist(cp); } catch { /* best-effort */ }
      return { ok: true, checkpoint: cp };
    } catch (e) {
      return { ok: false, error: String(e?.message || e) };
    }
  });
  const _restoreLatestGreen = restoreLatestGreen || (async (st, opts, deps) => {
    if (!st?.checkpoints?.length) return { ok: false, reason: 'no checkpoints' };
    const latest = st.checkpoints[st.checkpoints.length - 1];
    if (!latest?.ref) return { ok: false, reason: 'no ref' };
    try {
      if (deps.restore) await deps.restore({ ref: latest.ref, root: opts.root });
      return { ok: true, ref: latest.ref };
    } catch (e) {
      return { ok: false, error: String(e?.message || e) };
    }
  });

  const onGreen = async ({ verdict, taskId, graph, signal } = {}) => {
    if (!_isHonestGreen(verdict)) return { ok: false, skipped: true };
    const ctx = { taskId: taskId || 'loop-turn', root, graph, signal };
    const deps = { snapshot, persist };
    const r = await _captureCheckpoint(verdict, ctx, deps);
    if (r.ok) store.checkpoints.push(r.checkpoint);
    return r;
  };

  const onFail = async ({ signal } = {}) => {
    return _restoreLatestGreen(store, { root, signal }, { restore });
  };

  return { onGreen, onFail, store };
}
